import React, { useMemo, useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";

const ProductsTable = ({ products, onSort, sortColumn, onRowClick }) => {
  const { t } = useTranslation(["products"]);
  const [pageSize, setPageSize] = useState(20);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: t("ProductName"),
        sortable: true,
        filter: true,
        sort: sortColumn?.path === "name" ? sortColumn.order : null,
        flex: 2,
      },
      {
        field: "shortName",
        headerName: t("ProductShortName"),
        sortable: true,
        filter: true,
        sort: sortColumn?.path === "shortName" ? sortColumn.order : null,
        flex: 1,
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        filter: true,
        sort: sortColumn?.path === "active" ? sortColumn.order : null,
        headerClass: "ag-center-aligned-header",
        flex: 1,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
    ],
    [t, sortColumn]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 100,
    }),
    []
  );

  // Create a memoized row click handler
  const handleRowClick = useCallback(
    (params) => {
      if (onRowClick) {
        onRowClick(params.data);
      }
    },
    [onRowClick]
  );

  // Create a getRowId function to ensure stable row identification
  const getRowId = useCallback(
    (params) => params.data.productId.toString(),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Enable pagination for all tables regardless of size
      enabled: true,
      // Default page size
      pageSize: 20,
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
  }, []);

  return (
    <ThemeableAgGrid
      className="table-bold clickable"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={products}
      onSortChanged={onSort}
      onRowClicked={handleRowClick}
      getRowId={getRowId}
      animateRows={true}
      // Normal layout for pagination
      domLayout="autoHeight"
      // Enable pagination
      pagination={true}
      paginationPageSize={pageSize}
      paginationPageSizeSelector={paginationConfig.pageSizeOptions}
      onPaginationChanged={(event) => {
        const newPageSize = event.api.paginationGetPageSize();
        if (newPageSize !== pageSize) {
          handlePageSizeChange(newPageSize);
        }
      }}
      suppressPaginationPanel={false}
    />
  );
};

export default React.memo(ProductsTable);

